import { useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid2,
  Typography,
  useTheme,
} from "@mui/material";
import DashboardPanel from "./DashboardPanel";
import DashboardAreaStatus from "./DashboardAreaStatus";
import { MainContent, MainData } from "IF/interfaceType";

import { createTitle } from "Utils/format";
import { DataContext } from "Contexts/DataProvider";
import { UserRole, useSessionStorage } from "IF/userSession";
import { ViewContext } from "Contexts/ViewProvider";

export const Dashboard = ({ data }: { data: MainData }) => {
  const { getUserData } = useSessionStorage();
  const userData = getUserData();
  const { userRole } = userData;

  // レイアウト設定
  const [tableBodyHeight, setTableBodyHeight] = useState(600);
  const [xsWidth, setXsWidth] = useState(0);

  // ダークモード
  const theme = useTheme();

  // serialNo単位、エリア名単位切替用
  const context = useContext(ViewContext);
  if (!context) {
    throw new Error("ChildComponent must be used within an ViewProvider");
  }
  const { isName, setVisible, isAlertPrioritized, setIsAlertPriorityVisible } =
    context;
  const [showData, setShowData] = useState<MainContent[]>(
    isName ? data.areaNameDataList : data.serialNoDataList
  );

  // サイドバーデータ持ち回り
  const sidebarContext = useContext(DataContext);
  if (!sidebarContext) {
    throw new Error("ChildComponent must be used within an ViewProvider");
  }
  const { menuItems, setChoiceMenu } = sidebarContext;

  // 詳細画面用（スマホ・タブレット）
  // クリックされたエリアのインデックスを設定
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  // 選択パネル切替
  const handleAreaStatusClick = (index: number) => {
    setSelectedIndex(index);
    setVisible(false);
    setIsAlertPriorityVisible(false);
  };
  // 詳細画面→一覧に戻る
  const handleReturn = () => {
    setSelectedIndex(null);
    setVisible(true);
    setIsAlertPriorityVisible(true);
  };

  // タイトル設定
  useEffect(() => {
    if (menuItems.length > 1) {
      const index = UserRole.YKC !== userRole ? 0 : 1;
      document.title = createTitle(menuItems[index].text);
      setChoiceMenu(menuItems[index].text);
    }
  }, [userRole, menuItems, setChoiceMenu]);

  // 初期表示時処理
  useEffect(() => {
    const updateHeight = () => {
      const windowHeight = window.innerHeight - 112;
      setTableBodyHeight(windowHeight);
    };

    const updateXsWidth = () => {
      const windowWidth = document.documentElement.clientWidth - 50;
      setXsWidth(windowWidth);
    };

    setVisible(true);
    setIsAlertPriorityVisible(true);

    // 初期表示時とリサイズ時に計算
    updateHeight();
    window.addEventListener("resize", updateHeight);

    updateXsWidth();
    window.addEventListener("resize", updateXsWidth);
    // クリーンアップ
    return () => {
      window.removeEventListener("resize", updateHeight);
      window.removeEventListener("resize", updateXsWidth);
    };
  }, [setVisible, setIsAlertPriorityVisible]);

  // 表示切替
  useEffect(() => {
    // シリアルNo単位かエリア名単位か
    let showWk = isName ? data.areaNameDataList : data.serialNoDataList;

    if (isAlertPrioritized) {
      // アラートが発生しているエリア順に並べる
      const sortedData = [...showWk].sort((a, b) => b.dangerNum - a.dangerNum);
      setShowData(sortedData);
    } else {
      setShowData(showWk);
    }
  }, [isName, isAlertPrioritized, data]);

  useEffect(() => {
    console.log("=== Dashboard data debug ===");
    console.log(data);

    data.serialNoDataList.forEach((panel) => {
      panel.dashboardDataList.forEach((worker) => {
        console.log("tagId =", worker.tagId);
      });
    });
  }, [data]);

  return (
    <>
      {/* PC */}
      <Box sx={{ display: { xs: "none", sm: "none", md: "block" } }}>
        <Box
          sx={{ display: "flex", height: "100%", overflowX: "auto", gap: 1 }}
        >
          {showData.map((panel, index) => (
            <Box
              key={index}
              sx={{
                flexGrow: 1,
                minWidth: {
                  md: "calc(48% - 10px)",
                  lg: "calc(32% - 10px)",
                },
                maxWidth: {
                  md: "calc(48% - 10px)",
                  lg: "calc(32% - 10px)",
                },
                bgcolor: theme.palette.custom.panel,
                height: tableBodyHeight,
              }}
            >
              <DashboardPanel panel={panel} />
            </Box>
          ))}
        </Box>
      </Box>
      {/* スマホ・タブレット */}
      <Box sx={{ display: { xs: "block", sm: "block", md: "none" } }}>
        {selectedIndex === null ? (
          <Grid2 container spacing={1}>
            {showData.map((panel, index) => (
              <Grid2 size={{ xs: 12, sm: 6 }} key={index}>
                <Card
                  sx={{
                    flexGrow: 1,
                    bgcolor: theme.palette.custom.panel,
                    padding: "8px",
                    Width: xsWidth,
                    minWidth: "300px",
                  }}
                  onClick={() => handleAreaStatusClick(index)}
                >
                  <CardContent sx={{ p: 0.5 }}>
                    <DashboardAreaStatus panel={panel} />
                  </CardContent>
                </Card>
              </Grid2>
            ))}
          </Grid2>
        ) : (
          // 詳細画面
          <>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <Box
                sx={{
                  bgcolor: theme.palette.custom.panel,
                  padding: "3px",
                }}
              >
                <Typography fontWeight="fontWeightBold">
                  ＞他エリア状況
                </Typography>
                <Box sx={{ height: "50px", overflowY: "auto" }}>
                  {showData.map(
                    (row, index) =>
                      selectedIndex !== index && (
                        <Typography fontWeight="fontWeightRegular" key={index}>
                          {row.name} {row.dashboardDataList.length}人
                          {row.dangerNum > 0 && (
                            <span style={{ color: "red" }}>
                              {` ${row.dangerNum}人未使用`}
                            </span>
                          )}
                        </Typography>
                      )
                  )}
                </Box>
              </Box>
              <Box
                sx={{
                  flexGrow: 1,
                  bgcolor: theme.palette.custom.panel,
                  minHeight: tableBodyHeight - 105,
                }}
              >
                <DashboardPanel
                  panel={showData[selectedIndex]}
                  minusHeight={100}
                />
              </Box>
              <Box sx={{ textAlign: "end", verticalAlign: "bottom" }}>
                <Button
                  variant="contained"
                  color="primary"
                  sx={{
                    width: "100px",
                  }}
                  onClick={handleReturn}
                >
                  戻る
                </Button>
              </Box>
            </Box>
          </>
        )}
      </Box>
    </>
  );
};

export default Dashboard;
